import { toTimestamp } from '../../../utils/dateUtils';
import type { Conversation } from '../types';

export const getInitials = (name: string) => {
    const parts = name?.trim()?.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name?.substring(0, 2)?.toUpperCase();
};
const COLORS = [
    '#1976d2',
    '#388e3c',
    '#d32f2f',
    '#7b1fa2',
    '#f57c00',
    '#0097a7',
    '#5d4037',
    '#455a64'
] as const;
export const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return COLORS[Math.abs(hash) % COLORS.length];
};

export const formatTimeMsg = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    if (isYesterday) return 'Yesterday';
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

export const getConversationName = (conv: Conversation) => {
    if (conv.type === 'direct' && conv.otherUser) {
        return `${conv.otherUser.firstName} ${conv.otherUser.lastName}`;
    }
    if (conv.type === 'document') {
        return `${conv.documentType || 'Document'} #${conv.documentNumber || conv.documentId}`;
    }
    return conv.name || 'Group';
};

type SortableConversation = { [k in 'lastMessageAt' | 'createdAt']?: string | Date | number | null };

/**
 * Sorts conversations or chats by their latest activity (lastMessageAt or createdAt)
 * in descending order (most recent first).
 * @param a - The first conversation to compare
 * @param b - The second conversation to compare
 * @returns A number indicating the sort order
 */
export const sortConversationsByRecency = <T extends SortableConversation>(a: T, b: T): number => {
    const dateB = toTimestamp(b?.lastMessageAt || b?.createdAt, 0);
    const dateA = toTimestamp(a?.lastMessageAt || a?.createdAt, 0);
    return dateB - dateA;
};

/** WhatsApp-style "last seen today at 14:05" for a user who is not connected. */
export const formatLastSeen = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === now.toDateString()) return `last seen today at ${time}`;
    if (d.toDateString() === yesterday.toDateString()) return `last seen yesterday at ${time}`;
    return `last seen ${d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })} at ${time}`;
};
